import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Badge,
  makeStyles,
} from '@fluentui/react-components';
import { analyticsApi, surveysApi, assignmentsApi } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import PageHeader from '../components/PageHeader.tsx';
import StatCard from '../components/StatCard.tsx';
import StatusBadge from '../components/StatusBadge.tsx';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--space-md)',
    marginBottom: 'var(--space-lg)',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 'var(--space-lg)',
    marginBottom: 'var(--space-lg)',
    [MOBILE]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-lg)',
    // Allow the card to shrink inside grid/flex so wide children
    // (charts) never force horizontal page scroll on small screens.
    minWidth: 0,
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-text)',
    marginBottom: 'var(--space-md)',
    display: 'block',
  },
  chartWrap: {
    height: '280px',
    width: '100%',
  },
  activityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 'var(--space-lg)',
    [MOBILE]: {
      gridTemplateColumns: '1fr',
    },
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--space-sm) var(--space-md)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-light)',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'var(--color-background)',
    },
  },
  listItemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  listItemPrimary: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listItemSecondary: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
  },
  listStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
});

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const s = useStyles();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: overview } = useQuery('analytics-overview', analyticsApi.getOverview);
  const { data: surveys } = useQuery('surveys-dashboard', () => surveysApi.list({ limit: 5 }));
  const { data: assignments } = useQuery('assignments-dashboard', () => assignmentsApi.list({ limit: 10 }));
  const { data: engagement } = useQuery('user-engagement', () => analyticsApi.getUserEngagement(7));
  const { data: surveyAnalytics } = useQuery('survey-analytics', analyticsApi.getSurveyAnalytics);

  const firstName = user?.full_name?.split(' ')[0] || 'Admin';

  const tooltipStyle = {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    boxShadow: 'var(--shadow-md)',
    fontSize: '12px',
  };

  return (
    <div>
      <PageHeader
        title={`${getGreeting()}, ${firstName}`}
        subtitle="Here's an overview of your survey platform"
      />

      {/* Stats */}
      <div className={s.statsGrid}>
        {/* Colours match the lines in the engagement chart below:
            responses green, active users blue. */}
        <StatCard
          title="Responses Today"
          value={overview?.responses_today ?? 0}
          subtitle={`${overview?.responses_this_week ?? 0} this week`}
          color="var(--color-success)"
        />
        <StatCard
          title="Active Users Today"
          value={overview?.active_users_today ?? 0}
          subtitle={`${overview?.active_users_this_week ?? 0} this week`}
          color="var(--undp-primary)"
        />
        <StatCard
          title="Sessions Completed (7 Days)"
          value={overview?.sessions_completed_this_week ?? 0}
          subtitle={`${overview?.sessions_completed_total ?? 0} all time`}
          color="var(--undp-accent)"
        />
        <StatCard
          title="Active Surveys"
          value={overview?.active_surveys || 0}
          subtitle={`${overview?.total_surveys || 0} total`}
          color="var(--color-text)"
        />
      </div>

      {/* Charts */}
      <div className={s.chartsGrid}>
        <div className={s.card}>
          <Text className={s.cardTitle}>User Engagement (Last 7 Days)</Text>
          <div className={s.chartWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={engagement || []}>
                <XAxis dataKey="date" stroke="var(--color-text-tertiary)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-text-tertiary)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="active_users" stroke="#0468B1" strokeWidth={2} name="Active Users" dot={{ fill: '#0468B1', strokeWidth: 0, r: 3 }} />
                <Line type="monotone" dataKey="responses_submitted" stroke="#25903F" strokeWidth={2} name="Responses" dot={{ fill: '#25903F', strokeWidth: 0, r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={s.card}>
          <Text className={s.cardTitle}>Session Completion Rate by Survey</Text>
          <div className={s.chartWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={surveyAnalytics || []}>
                <XAxis dataKey="survey_title" stroke="var(--color-text-tertiary)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-text-tertiary)" fontSize={11} tickLine={false} axisLine={false} />
                {/* A bare "0%" reads as broken. Show the counts it came from,
                    so an untouched survey is legible as 0 of 0 rather than a failure. */}
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: any, _name: any, entry: any) => {
                    const { sessions_completed: done, sessions_started: all } = entry.payload;
                    return [
                      `${value}% (${done} of ${all} session${all === 1 ? '' : 's'})`,
                      'Completion Rate',
                    ];
                  }}
                />
                <Bar dataKey="completion_rate" fill="#0468B1" name="Completion Rate %" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className={s.activityGrid}>
        <div className={s.card}>
          <Text className={s.cardTitle}>Recent Assignments</Text>
          <div className={s.listStack}>
            {assignments?.slice(0, 5).map((assignment) => (
              <div key={assignment.id} className={s.listItem} onClick={() => navigate('/assignments')}>
                <div className={s.listItemContent}>
                  <span className={s.listItemPrimary}>{assignment.survey_title}</span>
                  <span className={s.listItemSecondary}>{assignment.user_name}</span>
                </div>
                <StatusBadge status={assignment.status} />
              </div>
            ))}
            {(!assignments || assignments.length === 0) && (
              <Text style={{ color: 'var(--color-text-secondary)', fontSize: '13px', padding: 'var(--space-md)' }}>
                No recent assignments
              </Text>
            )}
          </div>
        </div>

        <div className={s.card}>
          <Text className={s.cardTitle}>Recent Surveys</Text>
          <div className={s.listStack}>
            {surveys?.map((survey) => (
              <div key={survey.id} className={s.listItem} onClick={() => navigate('/surveys')}>
                <div className={s.listItemContent}>
                  <span className={s.listItemPrimary}>{survey.title}</span>
                  <span className={s.listItemSecondary}>
                    Created {new Date(survey.created_at).toLocaleDateString()}
                  </span>
                </div>
                <StatusBadge status={survey.status} />
              </div>
            ))}
            {(!surveys || surveys.length === 0) && (
              <Text style={{ color: 'var(--color-text-secondary)', fontSize: '13px', padding: 'var(--space-md)' }}>
                No surveys yet
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
